import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { productService } from '../../services/api';
import { isEmoji, renderEntityImage } from '../../utils/imageUtils';
import { C } from '../../styles/theme';
import { typeFromName, dataUrlToBlob, readableDetail } from './lib/imagePick';

const FOOD_EMOJIS = [
    '🍕','🍔','🌮','🌯','🥙','🥗','🍛','🍜','🍝','🍣',
    '🍱','🍲','🍳','🥘','🧆','🥟','🥠','🍤','🍗','🍖',
    '🥩','🥚','🧀','🍞','🥐','🥖','🥨','🧇','🥞','🧈',
    '🥓','🌭','🍟','🥪','🧁','🍰','🎂','🍩','🍪','🍫',
    '🍬','🍭','🍮','🍯','☕','🍵','🧃','🥤','🍺','🍷',
    '🍸','🍹','🧊','🍓','🍎','🍊','🍋','🍌','🍉','🍇',
    '🥑','🥦','🌽','🥕','🧅','🍆','🌶️','🫑','🥒','🍅',
];

const FONT = "'Plus Jakarta Sans', sans-serif";

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 2 * 1024 * 1024;

export default function IconPicker({ value, onChange, entityType, entityId }) {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('emoji');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [pendingEmoji, setPendingEmoji] = useState(value || null);
    const fileRef = useRef(null);
    // The chosen image's BYTES, captured at selection time.
    //
    // Upload used to re-read `fileRef.current.files[0]` when the button was
    // pressed — a separate user gesture, seconds after the pick. On iOS Safari
    // with limited photo access ("Safari can only access the items you
    // select") that File reference can go stale in between: the object still
    // exists and passes the type/size checks, but reading it yields nothing,
    // so the multipart body arrives with no usable `file` part and the server
    // answers 422 "Field required".
    //
    // That is not hypothetical — a pilot tenant (PIRI FLAME) hit it three
    // times on 2026-08-27 and has 0 of 72 products with an image, i.e. the
    // feature has never once worked for them. Capturing the bytes during the
    // selection handler, while access is still granted, removes the stale
    // reference from the flow entirely and costs nothing on other platforms.
    const capturedRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPendingEmoji(value || null);
            setPreview(null);
            capturedRef.current = null;
            setError(null);
            setActiveTab('emoji');
        }
    }, [open, value]);

    useEffect(() => {
        if (!open) return;
        const onKey = e => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const handleEmojiSelect = (emoji) => {
        setPendingEmoji(emoji);
        setError(null);
    };

    const handleSaveEmoji = () => {
        onChange(pendingEmoji);
        setOpen(false);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        capturedRef.current = null;
        if (!file) return;
        setError(null);

        // iOS Safari's photo picker can hand back a File with an EMPTY type
        // when photo access is limited. Fall back to the filename extension
        // rather than rejecting a perfectly good JPEG for having no MIME.
        const declaredType = file.type || typeFromName(file.name);
        if (!ALLOWED_TYPES.includes(declaredType)) {
            setError(t('icon_picker_invalid_format'));
            return;
        }
        if (file.size > MAX_SIZE) {
            setError(t('icon_picker_file_too_large'));
            return;
        }

        // Capture the bytes NOW, while access is still granted — see
        // capturedRef's note. The same read also feeds the preview, so this
        // is one read, not two.
        const reader = new FileReader();
        reader.onload = () => {
            setPreview(reader.result);
            try {
                capturedRef.current = new File(
                    [dataUrlToBlob(reader.result)],
                    file.name || 'image',
                    { type: declaredType },
                );
            } catch (err) {
                // If the capture fails for any reason, upload falls back to
                // the live input below rather than blocking the user.
                console.error('Failed to capture selected image bytes:', err);
                capturedRef.current = null;
            }
        };
        reader.onerror = () => setError(t('icon_picker_upload_failed'));
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        // Prefer the bytes captured at selection; the live input is only a
        // fallback for browsers where the capture did not complete.
        const file = capturedRef.current || fileRef.current?.files?.[0];
        if (!file || !entityId) return;
        if (!file.size) {
            // A zero-byte file is the stale-reference symptom. Say so, rather
            // than let the server answer 422 and show a generic failure.
            setError(t('icon_picker_reselect_image', {}, 'Could not read that image. Please choose it again.'));
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const result = await productService.uploadEntityImage(entityType, entityId, file);
            onChange(result.image_url);
            setPreview(null);
            setOpen(false);
        } catch (err) {
            setError(readableDetail(err?.response?.data?.detail, t('icon_picker_upload_failed')));
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        onChange(null);
        setPendingEmoji(null);
        setPreview(null);
        setError(null);
        setOpen(false);
    };

    // Trigger button
    const currentPreview = value ? renderEntityImage(value, 28) : null;

    return (
        <div style={{ fontFamily: FONT }}>
            {/* Trigger button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 14px', borderRadius: 8,
                        border: `1.5px solid ${C.border}`, background: C.bg,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600,
                        color: C.muted,
                    }}
                >
                    {currentPreview || (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                        </svg>
                    )}
                    {value ? t('icon_picker_change') : t('icon_picker_choose')}
                </button>
                {value && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        style={{
                            fontSize: 11, fontWeight: 600, color: C.red, background: C.redLight,
                            border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                            fontFamily: FONT,
                        }}
                    >
                        {t('icon_picker_remove')}
                    </button>
                )}
            </div>

            {/* Modal */}
            {open && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onClick={() => setOpen(false)}
                >
                    <div
                        style={{
                            background: C.white, borderRadius: 12, padding: 24,
                            width: 420, maxHeight: '80vh', overflowY: 'auto',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT }}>
                                {t('icon_picker_label')}
                            </h3>
                            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: 0, marginBottom: 14 }}>
                            {['emoji', 'upload'].map(tab => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => { setActiveTab(tab); setError(null); }}
                                    style={{
                                        flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: 0.8,
                                        border: `1px solid ${C.border}`,
                                        borderBottom: activeTab === tab ? `2px solid ${C.green}` : `1px solid ${C.border}`,
                                        background: activeTab === tab ? C.greenLight : C.white,
                                        color: activeTab === tab ? C.green : C.muted,
                                        cursor: 'pointer', fontFamily: FONT,
                                    }}
                                >
                                    {tab === 'emoji' ? t('icon_picker_emoji_tab') : t('icon_picker_upload_tab')}
                                </button>
                            ))}
                        </div>

                        {/* Emoji Grid */}
                        {activeTab === 'emoji' && (
                            <>
                                <div style={{
                                    display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4,
                                    maxHeight: 220, overflowY: 'auto', padding: 4,
                                    border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14,
                                }}>
                                    {FOOD_EMOJIS.map(emoji => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => handleEmojiSelect(emoji)}
                                            style={{
                                                fontSize: 22, padding: 4, border: 'none', borderRadius: 6,
                                                cursor: 'pointer',
                                                background: pendingEmoji === emoji ? C.greenLight : 'transparent',
                                                outline: pendingEmoji === emoji ? `2px solid ${C.green}` : 'none',
                                            }}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <button type="button" onClick={() => setOpen(false)}
                                        style={{ padding: '7px 14px', borderRadius: 7, border: `1.5px solid ${C.border}`, background: C.white, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                                        {t('cancel')}
                                    </button>
                                    <button type="button" onClick={handleSaveEmoji} disabled={!pendingEmoji}
                                        style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: pendingEmoji ? C.green : '#94a3b8', color: C.white, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                                        {t('save')}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Upload */}
                        {activeTab === 'upload' && (
                            <div style={{
                                border: `1px dashed ${C.border}`, borderRadius: 8, padding: 20,
                                textAlign: 'center',
                            }}>
                                {preview ? (
                                    <div style={{ marginBottom: 12 }}>
                                        <img src={preview} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />
                                    </div>
                                ) : null}
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handleFileSelect}
                                    style={{ marginBottom: 10 }}
                                />
                                <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
                                    {t('icon_picker_upload_hint')}
                                </div>
                                {preview && entityId && (
                                    <button
                                        type="button"
                                        onClick={handleUpload}
                                        disabled={uploading}
                                        style={{
                                            padding: '7px 18px', fontSize: 13, fontWeight: 700,
                                            background: C.green, color: C.white, border: 'none',
                                            borderRadius: 6, cursor: uploading ? 'not-allowed' : 'pointer',
                                            opacity: uploading ? 0.6 : 1, fontFamily: FONT,
                                        }}
                                    >
                                        {uploading ? t('icon_picker_uploading') : t('icon_picker_upload_btn')}
                                    </button>
                                )}
                                {!entityId && preview && (
                                    <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>
                                        {t('icon_picker_save_first')}
                                    </div>
                                )}
                            </div>
                        )}

                        {error && (
                            <div style={{ fontSize: 12, color: C.red, marginTop: 8 }}>{error}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
